import cn from "classnames";
import { useContext } from "react";
import {
  RadioProps as RadioPropsPrimitiveProps,
  Radio as RadioPrimitive,
  RadioGroupStateContext,
} from "react-aria-components";

import { getBorderRadiusProps } from "@/components/BorderRadius/BorderRadius";
import { getFocusOutlineProps } from "@/components/FocusOutline/FocusOutline";
import { InputBorder } from "@/components/InputBorder/InputBorder";
import { getInputBorderState } from "@/components/InputBorder/InputBorder.utils";
import { Stack } from "@/components/Stack/Stack";
import { mergeProps } from "@/utils/reactExtensions";

import styles from "./Radio.module.scss";

export interface RadioProps extends RadioPropsPrimitiveProps {
  /**
   * The value of the radio button, used when submitting an HTML form. See MDN.
   */
  value: RadioPropsPrimitiveProps["value"];

  /**
   * Whether the radio button is disabled or not.
   * Shows that a selection exists, but is not available in that circumstance.
   */
  isDisabled?: RadioPropsPrimitiveProps["isDisabled"];
  isInvalid?: boolean;

  /**
   * Whether the element has frame or not.
   */
  isFramed?: boolean;

  /**
   * Ref to radio element.
   */
  inputRef?: React.MutableRefObject<HTMLInputElement>;

  /** Whether the element should receive focus on render. */
  autoFocus?: RadioPropsPrimitiveProps["autoFocus"];
  children: React.ReactNode;
}

/**
 * Radio is an interactive element used for binary choices where there is only one answer, either ‘yes’ (selected) or
 * ‘no’ (unselected).
 *
 * They’re generally used within forms, and always within a group of items.
 *
 * ## Note
 * - Only one radio button can be selected within a group. The group items are mutually exclusive. Once a choice has been
 * selected, the user must make a choice within that group.
 * - It is recommended to use this in a `RadioGroup` component.
 *
 * ## Usage
 *
 * ```tsx
 *  <Radio value="val">My Radio Option</Radio>
 * ```
 */
export const Radio = /*#__PURE__*/ (function () {
  const Radio: React.FC<RadioProps> = ({ children, isFramed, className, ...props }) => {
    const state = useContext(RadioGroupStateContext);
    const isInvalid = state?.isInvalid || props.isInvalid;
    const isDisabled = state?.isDisabled || props.isDisabled;
    const isDefault = isInvalid === undefined;
    const isSelected = !!state.selectedValue && props.value === state.selectedValue;

    const renderContent = (
      <RadioPrimitive
        {...props}
        className={cn(styles.radio, { [styles.isFramed]: isFramed }, className?.toString())}
      >
        <Stack direction="row" spacing="xs" alignItems="center" className={styles.stack}>
          <div className={styles.iconContainer}>
            <InputBorder
              borderRadius="rounded"
              isHoverStateDisabled={isDisabled || !isDefault}
              state={getInputBorderState({ isInvalid, isDefault })}
              {...mergeProps(getFocusOutlineProps({}), {
                className: styles.radioDotContainer,
              })}
            >
              <div
                {...mergeProps(getBorderRadiusProps("rounded"), {
                  className: styles.radioDot,
                })}
              ></div>
            </InputBorder>
          </div>
          {children}
        </Stack>
      </RadioPrimitive>
    );

    return isFramed ? (
      <InputBorder
        isHoverStateDisabled={true}
        state={getInputBorderState({ isInvalid, isDefault, isSelected })}
        borderRadius="medium"
      >
        {renderContent}
      </InputBorder>
    ) : (
      renderContent
    );
  };

  Radio.displayName = "Radio";

  return Radio;
})();
